/**
 * 玩家间转账：求购（买家）、批准（卖家）、流水（全员）。
 *
 * ── 三条不能动的交互约束 ─────────────────────────────────────
 *
 * 1. **入口挂在对方的座位牌上**。「向谁买」这件事本来就是指着人说的；
 *    做成一颗独立按钮就得先弹一张选人列表，多一步，而且和牌桌上的人对不上号。
 *
 * 2. **上限在界面上就挡住**。可买额 = `min(对方筹码, 起始线 − 我的筹码)`，
 *    超了的档位根本不出现。让人填一个必被服务端钳掉的数、再回一条错误，
 *    等于让他白填一次。
 *
 * 3. **被求购方不弹窗**。他可能正在自己的回合上，一个模态盖住牌桌会让他直接超时。
 *    提示是顶栏里的一个角标，点开才是列表。
 *
 * ★ 红线 #37：上行只带 `toSeat`，自己的座位由连接身份解析。
 * ★ 红线 #38：`amount` 只是申请额，服务端会钳到三个上限的最小值，并在生效那一刻重算，
 *   所以界面上的数字一律说成「最多」。
 * ★ 红线 #41：批准 ≠ 划账。手牌进行中批准的排队到手牌边界，这段等待期两侧都要说出来。
 */
import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { PlayerView, SeatIndex, TransferRequestView } from '@poker/shared'

import { IconCheck, IconChips, IconClock, IconClose } from '../icons/index.js'
import { resolveAvatar } from '../table/avatar.js'
import { useUiStore } from '../store/index.js'
import { selectNetView, useNetStore } from '../transport/applyMessage.js'

import { transferErrorText } from './errorText.js'
import { formatExact, waitedText } from './topUpDerive.js'
import { botRemoval, botRemovalFeedback, type BotRemoval } from './roomDerive.js'
import {
  incomingTransfers,
  pendingIncomingCount,
  quickAmounts,
  showTransferQueue,
  transferLogLines,
  transferLogOf,
  seatProfile,
  transferTarget,
  type SeatProfileModel,
  type TransferTargetModel,
} from './transferDerive.js'
import { useRoomLink } from './roomLink.js'
import { SheetShell } from './SheetShell.js'
import { useTickingNow } from './useNow.js'
import './room.css'

/** 「等了多久 / 多久以前」的刷新间隔。分钟级文案，30s 一跳足够。 */
const WAIT_TICK_MS = 30_000

// ============================================================================
// 座位牌菜单
// ============================================================================

/**
 * 座位名片。点谁的头像/名牌就开这一张。
 *
 * ★ 开不开由 `seatProfile` 决定，不是 `transferTarget`：后者在「我没坐下」时恒为 null，
 *   拿它当判据的话，观战的人点谁都没反应 —— 而「这人是谁、头像长什么样」跟我坐没坐下无关。
 *   能不能买筹码只决定**那个按钮**出不出现。
 * ★ 房主点 AI 还多一行 Destructive「移出」（发 removeBot）。以前唯一的移出在等待室里，
 *   牌局进行中等待室不出现 —— 一个输光、不走的 AI 房主拿它毫无办法。
 */
export function SeatMenuSheet(): ReactNode {
  const sheet = useUiStore((s) => s.sheet)
  const seat = useUiStore((s) => s.sheetSeat)
  const openSheet = useUiStore((s) => s.openSheet)
  const closeSheet = useUiStore((s) => s.closeSheet)
  const showToast = useUiStore((s) => s.showToast)
  const view = useNetStore(selectNetView)
  const link = useRoomLink()
  const who = seatProfile(view, seat)

  const remove = useCallback((): void => {
    if (seat === null) return
    const current = useNetStore.getState().view
    const note = botRemovalFeedback(current, seat)
    link.send({ type: 'removeBot', seat })
    // 局外立刻离房，名片的主人没了：面板必须跟着关，否则下一个坐进这个座位的人会让它凭空弹出来
    closeSheet()
    if (note !== null) showToast(note, 'info')
  }, [closeSheet, link, seat, showToast])

  if (sheet !== 'seatMenu' || who === null) return null
  return (
    <SeatMenuPanel
      who={who}
      target={transferTarget(view, seat)}
      removal={botRemoval(view, seat)}
      onBuy={(s) => openSheet('transfer', s)}
      onRemove={remove}
      onClose={closeSheet}
    />
  )
}

export interface SeatMenuPanelProps {
  readonly who: SeatProfileModel
  readonly target: TransferTargetModel | null
  readonly removal: BotRemoval | null
  readonly onBuy: (seat: SeatIndex) => void
  readonly onRemove: () => void
  readonly onClose: () => void
}

/** 名片本体，只吃 props（node 测试里挂在 store 上的开合层静态渲染不出来）。 */
export function SeatMenuPanel({ who, target, removal, onBuy, onRemove, onClose }: SeatMenuPanelProps): ReactNode {
  const avatar = resolveAvatar(who.avatarSeed, who.accountId)

  return (
    <SheetShell label={who.nickname} onClose={onClose}>
      {/* 大图：牌桌上那颗头像只有 40 来像素，看不清画的是什么 */}
      <div className="seat-card">
        <span
          className="seat-card__avatar"
          data-av-kind={avatar.kind}
          data-av={avatar.kind === 'bot' ? avatar.variant : undefined}
          data-bot-variant={avatar.kind === 'bot' ? avatar.variant : undefined}
        >
          {avatar.kind === 'custom' || avatar.kind === 'bot' ? (
            // alt="" 是对的：名字就在下面那行，头像本身不承载信息
            <img className="seat-card__img" src={avatar.url} alt="" width={160} height={160} />
          ) : avatar.kind === 'emoji' ? (
            <span className="seat-card__emoji">{avatar.char}</span>
          ) : (
            <span className="seat-card__initial">{[...who.nickname][0] ?? ''}</span>
          )}
        </span>
        <strong className="seat-card__name">{who.nickname}</strong>
        <span className="seat-card__meta">
          <span className="num">{formatExact(who.stack)}</span>
          {who.isBot && <span className="seat-card__tag">AI</span>}
          {who.isHost && <span className="seat-card__tag">房主</span>}
        </span>
      </div>

      {target !== null &&
        (target.pending !== null || target.blocked === null ? (
          <button type="button" className="btn btn--quiet transfer-menu__item" onClick={() => onBuy(target.seat)}>
            <IconChips size={16} />
            向 TA 买筹码
          </button>
        ) : (
          // ★ 买不了就不给这颗按钮：点进去只会看到一句「买不了」。原因直接写在这里
          //   （向 AI 买时最常见：它筹码不多、或这局已经卖给你两次了）
          <p className="sheet__empty seat-card__why">{target.blocked}</p>
        ))}

      {removal !== null && (
        <button
          type="button"
          className="btn btn--quiet transfer-menu__item btn--destructive"
          disabled={removal === 'pending'}
          onClick={onRemove}
        >
          {removal === 'pending' ? '这手打完移出' : '移出'}
        </button>
      )}
    </SheetShell>
  )
}

// ============================================================================
// 买家侧：求购面板
// ============================================================================

export function TransferSheet(): ReactNode {
  const sheet = useUiStore((s) => s.sheet)
  const seat = useUiStore((s) => s.sheetSeat)
  const closeSheet = useUiStore((s) => s.closeSheet)
  const view = useNetStore(selectNetView)
  const target = transferTarget(view, seat)

  if (sheet !== 'transfer' || target === null) return null

  return (
    <SheetShell label="买筹码" onClose={closeSheet}>
      <header className="sheet__head">
        <strong>向 {target.nickname} 买</strong>
        <span className="sheet__sub num">起始线 {formatExact(target.startingStack)}</span>
      </header>

      <TransferFacts target={target} />

      {/* key=座位：换一个人求购时，上一次选的档位不该跟着过来 */}
      <TransferForm key={target.seat} target={target} bigBlind={view?.room.config.bigBlind ?? 0} />
    </SheetShell>
  )
}

function TransferFacts({ target }: { readonly target: TransferTargetModel }): ReactNode {
  return (
    <dl className="topup-facts">
      <div>
        <dt>TA 有</dt>
        <dd className="num">{formatExact(target.stack)}</dd>
      </div>
      <div>
        <dt>你有</dt>
        <dd className="num">{formatExact(target.myStack)}</dd>
      </div>
      <div>
        <dt>最多买</dt>
        <dd className="num">{formatExact(target.max)}</dd>
      </div>
    </dl>
  )
}

interface TransferFormProps {
  readonly target: TransferTargetModel
  readonly bigBlind: number
}

function TransferForm({ target, bigBlind }: TransferFormProps): ReactNode {
  const closeSheet = useUiStore((s) => s.closeSheet)
  const link = useRoomLink()
  const presets = quickAmounts(target.max, bigBlind)
  const [picked, setPicked] = useState<number | null>(null)
  const amount = picked === null ? target.max : Math.min(picked, target.max)

  const request = useCallback((): void => {
    if (amount <= 0) return
    // ★ 红线 #37：不带自己的座位。★ 红线 #38：amount 只是申请额，服务端还会再钳一次
    link.send({ type: 'requestTransfer', toSeat: target.seat, amount })
    closeSheet()
  }, [link, target.seat, amount, closeSheet])

  const cancel = useCallback((): void => {
    const id = target.pending?.requestId
    if (id === undefined) return
    link.send({ type: 'cancelTransfer', requestId: id })
    closeSheet()
  }, [link, target.pending, closeSheet])

  // 已经有一笔在途：这时该做的是等或撤，不是再发一笔（服务端会把旧的替换掉，
  // 但界面上连着两条「等批准」会让人以为买了两次）
  if (target.pending !== null) {
    return (
      <>
        <p className="transfer-state">
          {target.pending.status === 'approved' ? '已答应，本手后到账' : `等 ${target.nickname} 答应`}
        </p>
        <button type="button" className="btn btn--quiet" onClick={cancel}>
          撤回求购
        </button>
      </>
    )
  }

  if (target.blocked !== null) {
    return <p className="sheet__empty">{target.blocked}</p>
  }

  return (
    <>
      <div className="transfer-picks" role="group" aria-label="买多少">
        {presets.map((n) => (
          <button
            key={n}
            type="button"
            className={n === amount ? 'btn btn--quiet transfer-pick is-on' : 'btn btn--quiet transfer-pick'}
            aria-pressed={n === amount}
            onClick={() => setPicked(n)}
          >
            <span className="num">{formatExact(n)}</span>
          </button>
        ))}
      </div>

      <button type="button" className="btn btn--cta pressable" onClick={request}>
        向 TA 买 <span className="num">{formatExact(amount)}</span>
      </button>
    </>
  )
}

// ============================================================================
// 卖家侧：角标 + 待批准列表
// ============================================================================

export interface TransferQueueBadgeProps {
  /** 覆盖 store 里的视图（回放 / storybook / 单测用）；省略就订阅 store。 */
  readonly view?: PlayerView | null
}

/**
 * 顶栏里的求购角标。**不是弹窗**：被求购的人可能正在自己的回合上。
 *
 * 待处理时显示笔数；处理完、只剩等生效的那几笔时收成一个勾。
 */
export function TransferQueueBadge({ view: viewProp }: TransferQueueBadgeProps = {}): ReactNode {
  const storeView = useNetStore(selectNetView)
  const view = viewProp === undefined ? storeView : viewProp
  const openSheet = useUiStore((s) => s.openSheet)

  if (!showTransferQueue(view)) return null
  const pending = pendingIncomingCount(view)

  return (
    <button
      type="button"
      className={pending > 0 ? 'btn btn--bar topup-badge is-pending' : 'btn btn--bar topup-badge'}
      aria-label={pending > 0 ? `${pending} 笔求购等你答应` : '已答应，本手后到账'}
      onClick={() => openSheet('transferQueue')}
    >
      <IconChips size={16} />
      {pending > 0 ? <span className="num">{pending}</span> : <IconCheck size={14} />}
    </button>
  )
}

export function TransferQueueSheet(): ReactNode {
  const sheet = useUiStore((s) => s.sheet)
  const closeSheet = useUiStore((s) => s.closeSheet)
  const view = useNetStore(selectNetView)
  const link = useRoomLink()
  const now = useTickingNow(sheet === 'transferQueue', WAIT_TICK_MS)

  // 两条全局提示挂在这里：本组件在 RoomOverlays 里恒挂载，不会随面板开合装卸
  useTransferErrorToast()
  useTransferResultToast(view)

  const approve = useCallback(
    (requestId: string): void => {
      link.send({ type: 'approveTransfer', requestId })
    },
    [link],
  )
  const deny = useCallback(
    (requestId: string): void => {
      link.send({ type: 'denyTransfer', requestId })
    },
    [link],
  )

  if (sheet !== 'transferQueue' || view === null) return null
  const rows = incomingTransfers(view)

  return (
    <SheetShell label="求购" onClose={closeSheet}>
      <header className="sheet__head">
        <strong>有人向你买筹码</strong>
        <span className="sheet__sub num">你有 {formatExact(stackOf(view))}</span>
      </header>

      {rows.length === 0 ? (
        <p className="sheet__empty">没有待处理的求购</p>
      ) : (
        <ul className="topup-list" role="list">
          {rows.map((r) => (
            <TransferRow
              key={r.requestId}
              req={r}
              buyerStack={stackOfSeat(view, r.fromSeat)}
              now={now}
              onApprove={approve}
              onDeny={deny}
            />
          ))}
        </ul>
      )}
    </SheetShell>
  )
}

function stackOf(view: PlayerView): number {
  const seat = view.viewer.ownedSeat
  if (seat === null) return 0
  return stackOfSeat(view, seat)
}

function stackOfSeat(view: PlayerView, seat: SeatIndex): number {
  return view.seats.find((s) => s.index === seat)?.stack ?? 0
}

interface TransferRowProps {
  readonly req: TransferRequestView
  readonly buyerStack: number
  readonly now: number
  readonly onApprove: (requestId: string) => void
  readonly onDeny: (requestId: string) => void
}

function TransferRow({ req, buyerStack, now, onApprove, onDeny }: TransferRowProps): ReactNode {
  const approved = req.status === 'approved'
  const who = req.fromNickname === '' ? `${req.fromSeat + 1} 号位` : req.fromNickname
  return (
    <li className={approved ? 'topup-row topup-row--approved' : 'topup-row'}>
      <span className="topup-row__who">
        <span className="topup-row__name">{who}</span>
        <span className="topup-row__meta">
          {/* 判断依据就这三个数：他现在多少、要买多少、等了多久 */}
          <span className="num">{formatExact(buyerStack)}</span>
          <span aria-hidden="true"> · </span>
          <span className="num">最多 {formatExact(req.amount)}</span>
          <span aria-hidden="true"> · </span>
          {approved ? '本手后到账' : waitedText(req.requestedAt, now)}
        </span>
      </span>

      {approved ? (
        <span className="badge badge--host">已答应</span>
      ) : (
        <span className="topup-row__acts">
          <button
            type="button"
            className="btn btn--kick hit-expand"
            aria-label={`拒绝 ${who}`}
            onClick={() => onDeny(req.requestId)}
          >
            <IconClose size={18} />
          </button>
          <button
            type="button"
            className="btn btn--primary topup-ok"
            aria-label={`答应 ${who}`}
            onClick={() => onApprove(req.requestId)}
          >
            <IconCheck size={18} />
          </button>
        </span>
      )}
    </li>
  )
}

// ============================================================================
// 流水：全员可见
// ============================================================================

export interface TransferLogButtonProps {
  readonly view?: PlayerView | null
}

/**
 * 顶栏入口。**所有人**都看得见，不只是当事人 ——
 * 谁转给谁、多少、什么时候是防 chip dumping 的唯一手段（阳光比限制有效）。
 * 一笔都没有时不出现：空列表的入口只是噪音。
 */
export function TransferLogButton({ view: viewProp }: TransferLogButtonProps = {}): ReactNode {
  const storeView = useNetStore(selectNetView)
  const view = viewProp === undefined ? storeView : viewProp
  const openSheet = useUiStore((s) => s.openSheet)
  const count = transferLogOf(view).length

  if (count === 0) return null

  return (
    <button
      type="button"
      className="btn btn--bar topup-badge"
      aria-label={`转账记录，${count} 条`}
      onClick={() => openSheet('transferLog')}
    >
      <IconClock size={16} />
      <span className="num">{count}</span>
    </button>
  )
}

export function TransferLogSheet(): ReactNode {
  const sheet = useUiStore((s) => s.sheet)
  const closeSheet = useUiStore((s) => s.closeSheet)
  const view = useNetStore(selectNetView)
  const now = useTickingNow(sheet === 'transferLog', WAIT_TICK_MS)

  if (sheet !== 'transferLog') return null
  const lines = transferLogLines(view, now)

  return (
    <SheetShell label="转账记录" onClose={closeSheet}>
      <header className="sheet__head">
        <strong>转账记录</strong>
      </header>

      {lines.length === 0 ? (
        <p className="sheet__empty">还没有人转过筹码</p>
      ) : (
        <ul className="transfer-log" role="list">
          {lines.map((l) => (
            <li key={l.key} className="transfer-log__row">
              <span className="transfer-log__who">
                {/* 读作「买家 ← 卖家」：箭头就是筹码的流向 */}
                {l.to}
                <span className="transfer-log__arrow" aria-label="收到自">
                  ←
                </span>
                {l.from}
              </span>
              <span className="transfer-log__amt num">{formatExact(l.amount)}</span>
              <span className="transfer-log__ago">{l.ago}</span>
            </li>
          ))}
        </ul>
      )}
    </SheetShell>
  )
}

// ============================================================================
// 提示
// ============================================================================

/** 服务端拒绝 → Toast。拒绝原因由服务端给（带具体额度），本地不改写。 */
function useTransferErrorToast(): void {
  const lastError = useNetStore((s) => s.lastError)
  const clearError = useNetStore((s) => s.clearError)
  const showToast = useUiStore((s) => s.showToast)

  useEffect(() => {
    if (lastError === null) return
    if (lastError.code !== 'E_TRANSFER_NOT_ALLOWED' && lastError.code !== 'E_TRANSFER_NOT_FOUND') return
    showToast(transferErrorText(lastError.code, lastError.message), 'warn')
    clearError()
  }, [lastError, clearError, showToast])
}

/**
 * 和自己有关的那笔有结果时冒一条 Toast。
 *
 * 只订自己参与的那些：别人之间的转账在流水里看得到，弹出来只是噪音。
 * 「已答应待生效」不弹 —— 面板与角标已经改写成「本手后到账」，同一件事说两遍。
 */
function useTransferResultToast(view: PlayerView | null): void {
  const link = useRoomLink()
  const showToast = useUiStore((s) => s.showToast)
  const seat = view?.viewer.ownedSeat ?? null

  useEffect(() => {
    const sub = link.onMessage
    if (sub === undefined || seat === null) return undefined
    return sub((m) => {
      if (m.type !== 'transferResolved') return
      const iBuy = m.fromSeat === seat
      const iSell = m.toSeat === seat
      if (!iBuy && !iSell) return
      if (m.outcome === 'approved') {
        // 带 appliedAt 的那条才是真划账；无 appliedAt 的「已答应」由面板承载
        if (m.appliedAt === undefined) return
        const amount = formatExact(m.amount ?? 0)
        showToast(iBuy ? `买到筹码 +${amount}` : `转出筹码 −${amount}`, iBuy ? 'success' : 'info')
        return
      }
      if (m.outcome === 'denied' && iBuy) showToast('对方没答应这次求购', 'warn')
      else if (m.outcome === 'expired' && iBuy) showToast('求购超时，已作废', 'warn')
      // cancelled 是买家自己撤的（或有人离座了），不用再说一遍
    })
  }, [link, seat, showToast])
}

export default TransferSheet
